import React, { useState, useEffect } from "react";
import { auth } from "../config/firebaseConfig";
import { 
  signInWithEmailAndPassword, 
  createUserWithEmailAndPassword,
  GoogleAuthProvider, 
  signInWithPopup,
  sendEmailVerification,
  sendPasswordResetEmail 
} from "firebase/auth";
import { useNavigate, useLocation } from "react-router-dom";
import { useMediaQuery } from "@mui/material";
import CircularProgress from "@mui/material/CircularProgress";
import backgroundImage from "../assets/images/bg.png";
import {
  Box,
  Button,
  TextField,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tabs,
  Tab,
  IconButton,
  InputAdornment,
  Grow,
} from "@mui/material";
import { Visibility, VisibilityOff } from "@mui/icons-material";
import { jwtDecode } from "jwt-decode";
import { getApiUrl, getApiHeaders } from "../config/api";
import logoImage from "../assets/images/Logo.png";

const Auth = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const isMobile = useMediaQuery("(max-width:600px)");
  
  // Tab state
  const [activeTab, setActiveTab] = useState(0); // 0 = Login, 1 = Signup
  
  // Form states
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [loading, setLoading] = useState(false);
  
  // Login specific states
  const [showResendVerification, setShowResendVerification] = useState(false);
  const [unverifiedUser, setUnverifiedUser] = useState(null);
  
  // Forgot password states
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [resetEmail, setResetEmail] = useState("");
  const [resetLoading, setResetLoading] = useState(false);
  const [resetError, setResetError] = useState("");
  const [resetSuccess, setResetSuccess] = useState("");

  // Google SSO provider setup
  const googleProvider = new GoogleAuthProvider();
  googleProvider.setCustomParameters({
    prompt: 'select_account'  // Always show account selection
  });

  // Check for message from navigation
  useEffect(() => {
    if (location.state?.message) {
      setSuccess(location.state.message);
      setActiveTab(0); // Switch to login tab
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [location, navigate]);

  // Clear form when switching tabs
  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
    setEmail("");
    setPassword("");
    setError("");
    setSuccess("");
    setShowResendVerification(false);
  };

  // Google SSO handler
  const handleGoogleSignIn = async () => {
    setError("");
    setSuccess("");
    setLoading(true);
    
    try {
      // Use popup for both mobile and desktop
      const result = await signInWithPopup(auth, googleProvider);
      const user = result.user;
      
      if (user) {
        const idToken = await user.getIdToken();
        
        // Sync user with backend
        try {
          await fetch(getApiUrl("user/sync"), {
            method: "POST",
            headers: getApiHeaders({
              "Authorization": `Bearer ${idToken}`
            })
          });
        } catch (e) {
          console.error("Sync error:", e);
        }
        
        // Decode token and route by role
        const decoded = jwtDecode(idToken);
        const role = decoded.role || "USER";
        
        // Set localStorage BEFORE navigation
        localStorage.setItem("userRole", role);
        localStorage.setItem("authToken", idToken);
        
        // Small delay to ensure localStorage is set
        await new Promise(resolve => setTimeout(resolve, 100));
        
        // Navigate based on role
        if (role === "GSO") {
          navigate("/gso/dashboard", { replace: true });
        } else if (role === "CO-ADMIN" || role === "ADMIN") {
          navigate("/admin", { replace: true });
        } else {
          navigate("/user/home", { replace: true });
        }
      }
      setLoading(false);
    } catch (err) {
      console.error("Google sign-in error:", err);
      setError(`Google sign-in failed: ${err.message || "Please try again."}`);
      setLoading(false);
    }
  };

  // Login handler
  const handleLogin = async () => {
    setError("");
    setLoading(true);

    if (!email || !password) {
      setError("All fields are required.");
      setLoading(false);
      return;
    }

    try {
      console.log("🔵 [LOGIN] Starting login for:", email);
      const userCredential = await signInWithEmailAndPassword(auth, email, password);
      const user = userCredential.user;
      console.log("✅ [LOGIN] User authenticated successfully!");

      // Get token first so we can determine role prior to enforcing email verification
      const idToken = await user.getIdToken();
      let role = "USER";
      try {
        const decoded = jwtDecode(idToken);
        role = decoded.role || "USER";
        console.log("🔐 [LOGIN] User role:", role);
      } catch (e) {
        console.error("Token decode error:", e);
      }

      // Enforce email verification for password users EXCEPT CO-ADMIN (requested bypass)
      if (role !== "CO-ADMIN" && !user.emailVerified && user.providerData[0]?.providerId === 'password') {
        console.warn("⚠️ [LOGIN] Email not verified - blocking login");
        setUnverifiedUser(user);
        setShowResendVerification(true);
        setError("Please verify your email before logging in.");
        await auth.signOut();
        setLoading(false);
        return;
      }

      console.log("✅ [LOGIN] Email verification check passed");

      try {
        await fetch(getApiUrl("me"), {
          method: "GET",
          headers: getApiHeaders({
            "Authorization": `Bearer ${idToken}`
          })
        });
        console.log("✅ [LOGIN] Backend sync complete");
      } catch (e) {
        console.error("Me endpoint error:", e);
      }

      
      // Set localStorage BEFORE navigation
      localStorage.setItem("userRole", role);
      localStorage.setItem("authToken", idToken);
      console.log("💾 [LOGIN] Auth data saved to localStorage");
      
      // Small delay to ensure localStorage is set
      await new Promise(resolve => setTimeout(resolve, 100));
      
      // Clear loading before navigation
      setLoading(false);
      
      // Navigate based on role
      console.log("🚀 [LOGIN] Navigating to dashboard...");
      if (role === "GSO") {
        navigate("/gso/dashboard", { replace: true });
      } else if (role === "CO-ADMIN" || role === "ADMIN") {
        navigate("/admin", { replace: true });
      } else {
        navigate("/user/home", { replace: true });
      }
    } catch (err) {
      console.error("❌ [LOGIN] Login failed!");
      console.error("   Error Code:", err.code);
      console.error("   Error Message:", err.message);
      setError("Login failed. Please check your credentials.");
      setLoading(false);
    }
  };

  // Signup handler
  const handleSignup = async () => {
    setError("");
    setSuccess("");
    setLoading(true);
    
    if (!email || !password) {
      setError("All fields are required.");
      setLoading(false);
      return;
    }
    
    try {
      console.log("🔵 [SIGNUP] Starting user registration for:", email);
      const userCredential = await createUserWithEmailAndPassword(auth, email, password);
      const user = userCredential.user;
      console.log("✅ [SIGNUP] User account created successfully!");
      console.log("   User UID:", user.uid);
      console.log("   Email:", user.email);
      console.log("   Email Verified:", user.emailVerified);
      
      // Send email verification
      try {
        console.log("📧 [EMAIL] Attempting to send verification email...");
        console.log("   Recipient:", user.email);
        console.log("   Redirect URL:", window.location.origin + '/auth');
        
        await sendEmailVerification(user, {
          url: window.location.origin + '/auth',
          handleCodeInApp: false
        });
        
        console.log("✅ [EMAIL] Verification email sent successfully!");
        console.log("   ⚠️  User should check their inbox (and spam folder)");
        
        setSuccess("Account created! Please check your email to verify your account before logging in.");
        setEmail("");
        setPassword("");
        setLoading(false); // Clear loading state
        
        // Sign out the user until they verify their email
        await auth.signOut();
        console.log("🔓 [AUTH] User signed out - waiting for email verification");
        
        // Switch to login tab after 3 seconds
        setTimeout(() => {
          setActiveTab(0);
          setSuccess(""); // Clear success message when switching to login tab
        }, 3000);
      } catch (emailError) {
        console.error("❌ [EMAIL] Failed to send verification email!");
        console.error("   Error Code:", emailError.code);
        console.error("   Error Message:", emailError.message);
        console.error("   Full Error:", emailError);
        
        setError(`Account created but verification email failed to send: ${emailError.message}. Please try to resend verification email from login tab.`);
        setLoading(false); // Clear loading state on email error
        setTimeout(() => {
          setActiveTab(0);
        }, 4000);
      }
    } catch (err) {
      console.error("❌ [SIGNUP] Registration failed!");
      console.error("   Error Code:", err.code);
      console.error("   Error Message:", err.message);
      
      if (err.code === "auth/weak-password") {
        setError("Password should be at least 6 characters.");
      } else if (err.code === "auth/invalid-email") {
        setError("Please enter a valid email address.");
      } else if (err.code === "auth/email-already-in-use") {
        setError("An account with this email already exists. Please login instead.");
      } else {
        setError("Unable to create account. Please try again.");
      }
      setLoading(false);
    }
  };

  // Resend verification email
  const handleResendVerification = async () => {
    if (!unverifiedUser) {
      console.warn("⚠️ [RESEND] No unverified user stored - cannot resend email");
      setError("No user found. Please try logging in again.");
      return;
    }
    
    setLoading(true);
    setError("");
    setSuccess("");
    
    try {
      console.log("📧 [RESEND] Attempting to resend verification email...");
      console.log("   Recipient:", unverifiedUser.email);
      console.log("   User UID:", unverifiedUser.uid);
      
      await sendEmailVerification(unverifiedUser, {
        url: window.location.origin + '/auth',
        handleCodeInApp: false
      });
      
      console.log("✅ [RESEND] Verification email resent successfully!");
      setSuccess("Verification email sent! Please check your inbox and verify your email.");
      setShowResendVerification(false);
    } catch (err) {
      console.error("❌ [RESEND] Failed to resend verification email!");
      console.error("   Error Code:", err.code);
      console.error("   Error Message:", err.message);
      console.error("   Full Error:", err);
      
      if (err.code === 'auth/too-many-requests') {
        console.warn("⚠️ [RESEND] Rate limit hit - Firebase is blocking too many email requests");
        console.warn("   Wait time: 1-2 hours recommended");
        setError("Too many requests. Please wait a few minutes before trying again.");
      } else {
        setError("Failed to send verification email. Please try again later.");
      }
    } finally {
      setLoading(false);
    }
  };

  // Forgot password handler
  const handleForgotPassword = async () => {
    setResetError("");
    setResetSuccess("");
    
    if (!resetEmail) {
      setResetError("Please enter your email address.");
      return;
    }
    
    setResetLoading(true);
    
    try {
      await sendPasswordResetEmail(auth, resetEmail);
      setResetSuccess("Password reset email sent! Please check your inbox.");
      setResetEmail("");
      setTimeout(() => {
        setShowForgotPassword(false);
        setResetSuccess("");
      }, 3000);
    } catch (err) {
      if (err.code === 'auth/user-not-found') {
        setResetError("No account found with this email address.");
      } else if (err.code === 'auth/invalid-email') {
        setResetError("Please enter a valid email address.");
      } else if (err.code === 'auth/too-many-requests') {
        setResetError("Too many requests. Please try again later.");
      } else {
        setResetError("Failed to send reset email. Please try again.");
      }
    } finally {
      setResetLoading(false);
    }
  };

  return (
    <Box
      sx={{
        width: "100%",
        height: "100vh",
        minHeight: "-webkit-fill-available",
        position: "fixed",
        top: 0,
        left: 0,
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        backgroundImage: `url(${backgroundImage})`,
        backgroundSize: "cover",
        backgroundRepeat: "no-repeat",
        backgroundPosition: "left",
        backgroundColor: "#FBFFE9",
        overflow: "hidden",
      }}
    >
      {/* Logo - Desktop: Left side, Mobile: Top center */}
      {!isMobile ? (
        // Desktop Logo - positioned in the green space on the left
        <Box
          sx={{
            position: "absolute",
            left: "10%",
            top: "50%",
            transform: "translateY(-50%)",
            zIndex: 1,
          }}
        >
          <img 
            src={logoImage} 
            alt="U-Find Logo" 
            style={{
              width: "350px",
              height: "auto",
              filter: "drop-shadow(2px 2px 4px rgba(0,0,0,0.1))"
            }}
          />
        </Box>
      ) : (
        // Mobile Logo - positioned above the auth container
        <Box
          sx={{
            position: "absolute",
            top: "3%",
            left: "50%",
            transform: "translateX(-50%)",
            zIndex: 1,
          }}
        >
          <img 
            src={logoImage} 
            alt="U-Find Logo" 
            style={{
              width: "180px",
              height: "auto",
              filter: "drop-shadow(2px 2px 4px rgba(0,0,0,0.1))"
            }}
          />
        </Box>
      )}

      {/* Auth Form Container */}
      <Box
        sx={{
          position: "absolute",
          top: "50%",
          right: isMobile ? "5%" : "5%",
          transform: "translateY(-50%)",
          width: isMobile ? "90%" : "416px",
          maxHeight: isMobile ? "85vh" : "auto",
          overflowY: isMobile ? "auto" : "visible",
          display: "flex",
          flexDirection: "column",
          gap: 2,
          backgroundColor: "#FBFFE9",
          padding: isMobile ? "20px" : "30px",
          borderRadius: "15px",
        }}
      >
        {/* Tabs */}
        <Tabs
          value={activeTab}
          onChange={handleTabChange}
          centered
          sx={{
            mb: 2,
            "& .MuiTabs-indicator": {
              backgroundColor: "#a0c49d",
              height: "4px",
              borderRadius: "2px",
              transition: "all 0.3s ease-in-out",
            },
          }}
        >
          <Tab
            label="LOG IN"
            sx={{
              fontFamily: "Poppins, sans-serif",
              fontWeight: 600,
              fontSize: isMobile ? "24px" : "32px",
              color: activeTab === 0 ? "#516d4f" : "#a0c49d",
              transition: "all 0.3s ease-in-out",
              "&.Mui-selected": {
                color: "#516d4f",
                transform: "scale(1.05)",
              },
              "&:hover": {
                color: "#516d4f",
              },
            }}
          />
          <Tab
            label="SIGN UP"
            sx={{
              fontFamily: "Poppins, sans-serif",
              fontWeight: 600,
              fontSize: isMobile ? "24px" : "32px",
              color: activeTab === 1 ? "#516d4f" : "#a0c49d",
              transition: "all 0.3s ease-in-out",
              "&.Mui-selected": {
                color: "#516d4f",
                transform: "scale(1.05)",
              },
              "&:hover": {
                color: "#516d4f",
              },
            }}
          />
        </Tabs>

        {/* Form Content with Animation */}
        <Grow in={true} key={activeTab} timeout={500}>
          <Box>
            {/* Email Input */}
            <TextField
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="Email"
              variant="outlined"
              fullWidth
              sx={{
                mb: 1,
                "& .MuiOutlinedInput-root": {
                  height: isMobile ? "50px" : "61px",
                  borderRadius: isMobile ? "20px" : "22px",
                  fontSize: isMobile ? "16px" : "20px",
                  "& fieldset": {
                    borderWidth: "3px",
                    borderColor: "#a0c49d",
                  },
                },
              }}
            />

            {/* Password Input */}
            <TextField
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Password"
              type={showPassword ? "text" : "password"}
              variant="outlined"
              fullWidth
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => setShowPassword(!showPassword)}
                      edge="end"
                      sx={{
                        color: "#516d4f",
                        marginRight: isMobile ? "4px" : "8px",
                        "&:hover": {
                          backgroundColor: "rgba(160, 196, 157, 0.1)",
                        },
                      }}
                    >
                      {showPassword ? <Visibility /> : <VisibilityOff />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
              sx={{
                mb: 0.5,
                "& .MuiOutlinedInput-root": {
                  height: isMobile ? "50px" : "61px",
                  borderRadius: isMobile ? "20px" : "22px",
                  fontSize: isMobile ? "16px" : "20px",
                  paddingRight: isMobile ? "8px" : "12px",
                  "& fieldset": {
                    borderWidth: "3px",
                    borderColor: "#a0c49d",
                  },
                },
              }}
            />

            {/* Forgot Password Link (Login only) */}
            {activeTab === 0 && (
              <Box sx={{ display: "flex", justifyContent: "flex-end", mb: 1.5, mt: 0.25 }}>
                <Typography
                  component="span"
                  onClick={() => setShowForgotPassword(true)}
                  sx={{ 
                    color: "#516d4f", 
                    fontSize: isMobile ? "13px" : "14px", 
                    cursor: "pointer",
                    textDecoration: "underline",
                    "&:hover": {
                      color: "#8ab287"
                    }
                  }}
                >
                  Forgot Password?
                </Typography>
              </Box>
            )}

            {/* Spacer for Signup tab to maintain consistent container height */}
            {activeTab === 1 && (
              <Box sx={{ mb: 1.5, mt: 0.25, height: isMobile ? "20px" : "22px" }} />
            )}

            {/* Success Message */}
            {success && (
              <Typography sx={{ textAlign: "center", color: "#2e7d32", fontWeight: 600, fontSize: isMobile ? "14px" : "16px", mb: 1 }}>
                {success}
              </Typography>
            )}

            {/* Error Message */}
            {error && (
              <Typography color="error" sx={{ textAlign: "center", fontSize: isMobile ? "14px" : "16px", mb: 1 }}>
                {error}
              </Typography>
            )}

        {/* Resend Verification Button (Login only) */}
        {activeTab === 0 && showResendVerification && (
          <Button
            onClick={handleResendVerification}
            variant="outlined"
            fullWidth
            disabled={loading}
            sx={{
              height: isMobile ? "50px" : "55px",
              borderRadius: isMobile ? "25px" : "22px",
              borderColor: "#a0c49d",
              color: "#516d4f",
              fontWeight: 600,
              fontSize: isMobile ? "16px" : "18px",
              mb: 1,
              "&:hover": {
                borderColor: "#8ab287",
                backgroundColor: "#f1f8f1",
              },
            }}
          >
            Resend Email Verification
          </Button>
        )}

        {/* Login/Signup Button */}
        <Button
          onClick={activeTab === 0 ? handleLogin : handleSignup}
          variant="contained"
          fullWidth
          disabled={loading}
          sx={{
            height: isMobile ? "60px" : "61px",
            borderRadius: isMobile ? "30px" : "22px",
            backgroundColor: loading ? "#8ab287" : "#a0c49d",
            "&:hover": {
              backgroundColor: "#8ab287",
            },
            mb: 2,
          }}
        >
          {loading ? (
            <CircularProgress size={24} sx={{ color: "#fbffe8" }} />
          ) : (
            <Typography sx={{ fontWeight: 600, color: "#fbffe8", fontSize: isMobile ? "24px" : "32px" }}>
              {activeTab === 0 ? "LOG IN" : "SIGN UP"}
            </Typography>
          )}
        </Button>

        {/* Google SSO Button */}
        <Button
          onClick={handleGoogleSignIn}
          variant="outlined"
          fullWidth
          disabled={loading}
          sx={{
            height: isMobile ? "60px" : "61px",
            borderRadius: isMobile ? "30px" : "22px",
            color: "#516d4f",
            borderColor: "#a0c49d",
            backgroundColor: "#fff",
            mb: 2,
            fontWeight: 600,
            fontSize: isMobile ? "20px" : "28px",
            textTransform: "none",
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 1.5,
            minWidth: 0,
            maxWidth: '100%',
            overflow: 'hidden',
            "&:hover": {
              backgroundColor: "#f1f1f1",
              borderColor: "#8ab287",
            },
          }}
        >
          <span style={{ display: 'flex', alignItems: 'center', marginRight: 12, flexShrink: 0 }}>
            <svg width={isMobile ? 28 : 32} height={isMobile ? 28 : 32} viewBox="0 0 48 48" style={{ display: 'block' }}>
              <g>
                <path fill="#FBBC05" d="M43.6 20.5h-1.9V20H24v8h11.3c-1.6 4.3-5.7 7-11.3 7-6.6 0-12-5.4-12-12s5.4-12 12-12c2.7 0 5.2.9 7.2 2.4l6-6C36.1 5.1 30.4 3 24 3 12.9 3 4 11.9 4 23s8.9 20 20 20c11 0 19.7-8 19.7-20 0-1.3-.1-2.2-.3-3.5z"/>
                <path fill="#EA4335" d="M6.3 14.7l6.6 4.8C14.3 16.1 18.7 13 24 13c2.7 0 5.2.9 7.2 2.4l6-6C36.1 5.1 30.4 3 24 3 15.3 3 7.9 8.6 6.3 14.7z"/>
                <path fill="#34A853" d="M24 43c5.4 0 10-1.8 13.3-4.9l-6.1-5c-2 1.4-4.5 2.2-7.2 2.2-5.6 0-10.3-3.8-12-9l-6.5 5C7.8 39.4 15.3 43 24 43z"/>
                <path fill="#4285F4" d="M43.6 20.5h-1.9V20H24v8h11.3c-0.7 2-2.1 3.7-4.1 4.9l6.1 5c3.6-3.3 5.7-8.1 5.7-13.4 0-1.3-.1-2.2-.3-3.5z"/>
              </g>
            </svg>
          </span>
          <span style={{
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            minWidth: 0,
            fontSize: isMobile ? 18 : 24,
            fontWeight: 600,
            flex: 1,
            textAlign: 'center',
            display: 'block',
          }}>
            Sign {activeTab === 0 ? 'in' : 'up'} with Google
          </span>
        </Button>
          </Box>
        </Grow>
      </Box>

      {/* Forgot Password Dialog */}
      <Dialog 
        open={showForgotPassword} 
        onClose={() => {
          setShowForgotPassword(false);
          setResetEmail("");
          setResetError("");
          setResetSuccess("");
        }}
        PaperProps={{
          sx: {
            borderRadius: "15px",
            padding: isMobile ? "10px" : "20px",
            backgroundColor: "#FBFFE9",
            minWidth: isMobile ? "90%" : "400px"
          }
        }}
      >
        <DialogTitle sx={{ 
          fontFamily: "Poppins, sans-serif",
          fontWeight: 600,
          color: "#516d4f",
          fontSize: isMobile ? "24px" : "28px",
          textAlign: "center",
          pb: 1
        }}>
          Reset Password
        </DialogTitle>
        <DialogContent>
          <Typography sx={{ 
            color: "#516d4f", 
            fontSize: isMobile ? "14px" : "16px",
            mb: 2,
            textAlign: "center"
          }}>
            Enter your email address and we'll send you a link to reset your password.
          </Typography>
          
          <TextField
            value={resetEmail}
            onChange={(e) => setResetEmail(e.target.value)}
            placeholder="Email"
            variant="outlined"
            fullWidth
            autoFocus
            sx={{
              mb: 2,
              "& .MuiOutlinedInput-root": {
                height: isMobile ? "50px" : "55px",
                borderRadius: "12px",
                fontSize: isMobile ? "16px" : "18px",
                "& fieldset": {
                  borderWidth: "2px",
                  borderColor: "#a0c49d",
                },
              },
            }}
          />

          {resetError && (
            <Typography color="error" sx={{ textAlign: "center", fontSize: isMobile ? "14px" : "16px", mb: 1 }}>
              {resetError}
            </Typography>
          )}

          {resetSuccess && (
            <Typography sx={{ textAlign: "center", color: "#2e7d32", fontWeight: 600, fontSize: isMobile ? "14px" : "16px", mb: 1 }}>
              {resetSuccess}
            </Typography>
          )}
        </DialogContent>
        <DialogActions sx={{ justifyContent: "center", pb: 2, gap: 2 }}>
          <Button
            onClick={() => {
              setShowForgotPassword(false);
              setResetEmail("");
              setResetError("");
              setResetSuccess("");
            }}
            disabled={resetLoading}
            sx={{
              borderRadius: "12px",
              backgroundColor: "#e0e0e0",
              color: "#516d4f",
              fontWeight: 600,
              fontSize: isMobile ? "16px" : "18px",
              px: 3,
              py: 1,
              "&:hover": {
                backgroundColor: "#d0d0d0",
              },
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleForgotPassword}
            disabled={resetLoading}
            sx={{
              borderRadius: "12px",
              backgroundColor: resetLoading ? "#8ab287" : "#a0c49d",
              color: "#fbffe8",
              fontWeight: 600,
              fontSize: isMobile ? "16px" : "18px",
              px: 3,
              py: 1,
              "&:hover": {
                backgroundColor: "#8ab287",
              },
            }}
          >
            {resetLoading ? "Sending..." : "Send Reset Link"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Auth;
